import ImageWithFallback from 'components/ImageWithFallback';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { TSubscriptions } from 'stores/UserStore';
import AboutUsModule from '../../about-us/AboutUsModule';
import AboutUsSiteContainer from '../../about-us/AboutUsSiteContainer';
import Edit from '../../svg/Edit16.svg';
import { LOADING_TIMEOUT } from '../common/constants';
import EditProfileModal from './EditProfileModal';
import styles from './HeaderProfile.module.css';
import { NEW_MASCOTS } from '@daymade/common-lib/constants';

// Temp once avatars have been updated on user profiles
export const mascotMapping: Record<string, string> = {
  'https://site-assets.plasmic.app/1be4ecac547f167f89b52477bbfc0f80.png':
    NEW_MASCOTS[1],
  'https://site-assets.plasmic.app/2af9da864833104acef88d878ff48441.png':
    NEW_MASCOTS[1],
  'https://site-assets.plasmic.app/65c13415d8015ec78b236d8bd1c4dd5f.png':
    NEW_MASCOTS[2],
  'https://site-assets.plasmic.app/83b7e5def1c5f12de6bb03cf73bc9eb6.png':
    NEW_MASCOTS[2],
  'https://site-assets.plasmic.app/a35c7a43ca97c2c8f4b7dc1fe2284571.png':
    NEW_MASCOTS[3],
  'https://site-assets.plasmic.app/acbc2ab9157ee70eb758a4b3c6b7a858.png':
    NEW_MASCOTS[3],
  'https://site-assets.plasmic.app/bbfb732488054fa23336d142df19ebdf.png':
    NEW_MASCOTS[4],
  'https://site-assets.plasmic.app/f62915af26eb28527a4f2982028dc0ae.png':
    NEW_MASCOTS[4],
};

const HeaderProfile = observer(({ loading }: { loading?: boolean }) => {
  const { currentUser, subscriptions, initialized } = useUserStore();
  const { isMobile } = useIsMobile();

  const [headerLoading, setHeaderLoading] = useState(true);
  const [subscription, setSubscription] = useState<TSubscriptions[number]>();
  const [name, setName] = useState<string>();
  const [useNewAvatar, setUseNewAvatar] = useState<boolean>();
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(
    currentUser?.avatarLocation,
  );
  const randomAvatar = useMemo(
    () =>
      Object.values(mascotMapping)[
        Math.floor(Math.random() * Object.values(mascotMapping).length)
      ],
    [],
  );

  useEffect(() => {
    const activeSubscription = subscriptions.find(
      (sub) =>
        sub.subscription.status === 'ACTIVE' ||
        sub.subscription.status === 'SNOOZED' ||
        sub.subscription.status === 'FAILING',
    );

    setSubscription(activeSubscription);
  }, [subscriptions, initialized]);

  useEffect(() => {
    setUseNewAvatar(
      (currentUser?.avatarLocation &&
        currentUser?.avatarLocation.includes('plasmic')) ||
        !currentUser?.avatarLocation,
    );
    setCurrentAvatar(currentUser?.avatarLocation);
    setName(currentUser?.firstName);
  }, [currentUser, initialized]);

  useEffect(() => {
    if (initialized && loading === undefined) {
      return setHeaderLoading(false);
    }

    if (initialized && !loading) {
      setTimeout(() => setHeaderLoading(false), LOADING_TIMEOUT);
    }
  }, [initialized, loading]);

  const openEditProfileModal = (open: boolean) => {
    setEditProfileModalOpen(open);
  };

  const avatar = useNewAvatar
    ? currentAvatar
      ? (mascotMapping[currentAvatar] ?? randomAvatar)
      : randomAvatar
    : (currentAvatar ?? randomAvatar);

  const subscriptionDrawTypePlanCode =
    subscription?.drawSchedulePlans[0].drawTypePlan.code;
  const memberPrefixText =
    subscriptionDrawTypePlanCode === 'PREMIUM'
      ? 'Premium'
      : subscriptionDrawTypePlanCode === 'LITE'
        ? 'Lite'
        : subscriptionDrawTypePlanCode === 'FREEMIUM'
          ? 'Free'
          : null;

  return (
    <AboutUsModule
      background={'tealGradient'}
      withReducedPadding
      extendBackgroundBelow
    >
      <EditProfileModal
        openModal={editProfileModalOpen}
        closeModal={() => setEditProfileModalOpen(false)}
        currentAvatar={avatar}
      />
      <AboutUsSiteContainer>
        <div className={styles.headerWrapper}>
          <div className={styles.avatar}>
            {headerLoading ? (
              <Skeleton
                circle
                width={isMobile ? 67 : 98}
                height={isMobile ? 67 : 98}
              />
            ) : (
              <ImageWithFallback
                src={avatar}
                fallbackSrc={NEW_MASCOTS[1]}
                alt="User profile icon"
                layout="fill"
                objectFit="cover"
              />
            )}
          </div>
          <div className={styles.textContent}>
            <div>
              <strong className="aboutUsTypographyHeaderLarge">
                {headerLoading ? (
                  <Skeleton width={'50%'} borderRadius={12} />
                ) : (
                  <>Hey{name ? `, ${name}` : ''} &#128075;</>
                )}
              </strong>
            </div>
            <div className={styles.links}>
              {subscription ? (
                <div
                  className={`${styles.membershipLink} aboutUsTypographyHeaderMedium`}
                >
                  {headerLoading ? (
                    <Skeleton width={200} height={28} borderRadius={12} />
                  ) : (
                    <strong>{memberPrefixText} Member</strong>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles.editProfile}>
            <button
              className={styles.editProfileButton}
              onClick={() => openEditProfileModal(true)}
            >
              {headerLoading ? (
                <Skeleton
                  width={isMobile ? 25 : 100}
                  height={isMobile ? 25 : 24}
                  borderRadius={isMobile ? 4 : 24}
                />
              ) : (
                <>
                  <div
                    className={`aboutUsTypographyBodySmall textDecorationUnderline ${styles.editProfileText}`}
                  >
                    <>Edit profile</>
                  </div>
                  <div className={styles.editProfileIcon}>
                    <Edit />
                  </div>
                </>
              )}
            </button>
          </div>
        </div>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
});

export default HeaderProfile;
